/**
 * O índice lateral das telas 10.1 e 2.4: UM elemento, duas apresentações.
 *
 * O protótipo mostra um card fixo no desktop e um `<details>` recolhível no mobile.
 * A primeira tentativa renderizou os dois e escondeu um por CSS — e duplicou cada
 * campo e cada botão no DOM: dois controles com o mesmo rótulo, e um leitor de tela
 * lendo tudo duas vezes. Então há um `<details>` só.
 *
 * **O `open` é decidido em JavaScript, não em CSS.** Um `display: block` no filho de
 * um `<details>` fechado não o revela: o navegador esconde o conteúdo pelo mecanismo
 * do próprio elemento, não por `display`. Perseguir isso com `!important` seria
 * brigar com o agente de usuário; abrir o elemento de verdade no desktop é o que ele
 * espera que se faça.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Icone } from '../shell/icone.tsx'

/**
 * Responde a uma media query e ACOMPANHA a troca de tamanho.
 *
 * O índice lateral usa `lg` (1024px); a barra lateral do shell usa `md` (768px), que é
 * onde ela deixa de deslizar por cima e passa a ficar fixa. São a mesma pergunta com
 * limiares diferentes — daí o parâmetro (lote U2).
 */
export function useConsultaDeTela(consulta: string): boolean {
  const [combina, setCombina] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia(consulta).matches,
  )
  useEffect(() => {
    const lista = window.matchMedia(consulta)
    const responder = (evento: MediaQueryListEvent): void => {
      setCombina(evento.matches)
    }
    lista.addEventListener('change', responder)
    setCombina(lista.matches)
    return () => {
      lista.removeEventListener('change', responder)
    }
  }, [consulta])
  return combina
}

/** A partir de `lg` (1024px) o índice fica sempre aberto, como no protótipo. */
export function useTelaLarga(): boolean {
  return useConsultaDeTela('(min-width: 1024px)')
}

export function IndiceLateral({
  rotulo,
  className = '',
  children,
  aoEscolher,
}: {
  /** O que o resumo mostra no mobile — o item aberto, para a pessoa saber onde está. */
  rotulo: string
  className?: string
  children: ReactNode
  /** Sinal de que algo foi escolhido: no mobile, o índice se fecha e o conteúdo aparece. */
  aoEscolher?: unknown
}) {
  const larga = useTelaLarga()
  const [abertoNoMobile, setAbertoNoMobile] = useState(false)

  // Escolher um item fecha o índice no celular — é o que a spec 2.4 descreve, e o
  // que evita a pessoa ter de rolar por cima da lista para ver o que escolheu.
  useEffect(() => {
    setAbertoNoMobile(false)
  }, [aoEscolher])

  return (
    <details
      className={`dv-indice h-fit ${className}`}
      open={larga || abertoNoMobile}
      onToggle={(evento) => {
        if (!larga) setAbertoNoMobile(evento.currentTarget.open)
      }}
    >
      <summary className="flex items-center justify-between gap-2 cursor-pointer text-[13px] font-medium text-navy">
        {rotulo}
        <Icone nome="chevron-down" className="w-4 h-4 text-muted" />
      </summary>
      <div className="mt-3 lg:mt-0">{children}</div>
    </details>
  )
}
